// @ts-strict-ignore
import type PlayClient from '@empathybroker/tsclient-play'
import type { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import type { Action } from '@empathybroker/tsclient-play/dist/models/action'
import type { ToolConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/tool-config'
import type { ToolsConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/tools-config'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { List } from '@empathybroker/tsclient-play/dist/models/list'
import type { Page as PlayPage } from '@empathybroker/tsclient-play/dist/models/page'
import type { PlayResponse } from '@empathybroker/tsclient-play/dist/models/play-response'
import type { RequestFilters as PlayFilters } from '@empathybroker/tsclient-play/dist/models/request-filters'
import type { SchedulableRequestFilters } from '@empathybroker/tsclient-play/dist/models/schedulable-request-filters'
import type { Sort as PlaySort } from '@empathybroker/tsclient-play/dist/models/sort'
import type { AccessTokenProvider } from '@empathyco/playboard-core-domain'
import type { ActionContext } from 'vuex'
import type { CrudDataTypeEnum } from '../../../enums'
import type {
  ActionClass,
  CrudState,
  Dictionary,
  Filter,
  Pagination,
  RegularFilter,
  RegularFilterSelectedValues,
  Sort,
} from '../../../models'
import type { ContextProvider } from '../../../providers'

import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { EndpointEnum } from '@empathybroker/tsclient-play/dist/enums/endpoint.enum'

import { SortDirectionEnum as PlaySortDirectionEnum } from '@empathybroker/tsclient-play/dist/enums/sort-direction.enum'

import { CONTAINER } from '../../../config'

import { TYPES } from '../../../config/inversify.types'
import {
  ActiveFilterEnum,
  EnabledFilterEnum,
  FilterIdEnum,
  FilterTypeEnum,
  SortDirectionEnum,
} from '../../../enums'
import { CONFIG_CONSTS } from '../../../store/config/config.consts'

export class FetchDataAction implements ActionClass {
  static async execute(
    this: void,
    { rootGetters }: ActionContext<CrudState, any>,
    payload: {
      dataType: CrudDataTypeEnum
      filters?: Dictionary<Filter>
      requestFilters?: Record<string, string>
      pagination?: Pagination
      sort?: Sort
      query?: string
      strictMatching?: boolean
      extraUrlParameters?: Record<string, any>
    },
  ): Promise<PlayResponse<List<Action>>> {
    const { domain } = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
    const serviceClient = FetchDataAction.getServiceClient(payload.dataType)
    const authToken = await CONTAINER.get<AccessTokenProvider>(TYPES.AccessTokenProvider)()
    const instanceConfig: Instance =
      rootGetters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.currentInstance}`]
    const toolsConfig: ToolsConfig =
      instanceConfig?.domains[domain as DomainEnum.SEARCH | DomainEnum.BROWSE]?.tools
    let toolConfig: ToolConfig = Object.assign(
      {},
      !!toolsConfig && toolsConfig.base,
      !!toolsConfig && toolsConfig[payload.dataType],
    )
    toolConfig = {
      ...toolConfig,
      pivotIds: toolConfig.pivotIds || [],
    }
    const endpoint: EndpointEnum = FetchDataAction.getPlayEndpointFromDomain()
    const playFilters = payload.filters
      ? FetchDataAction.getPlayFilters(payload.filters, toolConfig.pivotIds)
      : undefined
    return (serviceClient as PlayClient).list(
      authToken,
      payload.dataType as ActionTypeEnum,
      Object.assign({}, playFilters, payload.requestFilters),
      payload.pagination ? FetchDataAction.getPlayPage(payload.pagination) : undefined,
      payload.sort ? FetchDataAction.getPlaySort(payload.sort) : undefined,
      payload.query,
      payload.strictMatching,
      endpoint,
      payload.extraUrlParameters,
    )
  }

  static getPlayEndpointFromDomain(): EndpointEnum {
    const { domain } = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
    return {
      [DomainEnum.SEARCH]: EndpointEnum.SEARCH,
      [DomainEnum.BROWSE]: EndpointEnum.BROWSE,
    }[domain]
  }

  static getServiceClient(dataType: CrudDataTypeEnum): PlayClient {
    return CONTAINER.get<() => PlayClient>(TYPES.PlayClient)()
  }

  static getPlayPage(pagination: Pagination): PlayPage {
    return {
      start: (pagination.page - 1) * pagination.itemsPerPage,
      rows: pagination.itemsPerPage,
    }
  }

  static getPlaySort(sort: Sort): PlaySort {
    if (!!sort.field && !!sort.direction) {
      return {
        field: sort.field,
        direction: {
          [SortDirectionEnum.ASCENDING]: PlaySortDirectionEnum.ASCENDING,
          [SortDirectionEnum.DESCENDING]: PlaySortDirectionEnum.DESCENDING,
        }[sort.direction],
      }
    }
  }

  private static getPlayFilters(filters: Dictionary<Filter>, pivotsIds: string[]): PlayFilters {
    const playFilters: PlayFilters = {
      pivots: {},
      filters: {},
    }
    Object.values(filters)
      .filter(
        (filter: Filter) =>
          filter.config.type === FilterTypeEnum.REGULAR &&
          !!(filter.selectedValues as RegularFilterSelectedValues).length,
      )
      .forEach((filter: RegularFilter) => {
        if (filter.config.id === FilterIdEnum.ACTIVE) {
          ;(playFilters as SchedulableRequestFilters).active =
            filter.selectedValues[0] === ActiveFilterEnum.ACTIVE
        } else if (filter.config.id === FilterIdEnum.ENABLED) {
          playFilters.enabled = filter.selectedValues[0] === EnabledFilterEnum.ENABLED
        } else {
          const filterId = filter.config.toolingId || filter.config.id
          const filterValue = filter.selectedValues.map((selectedValue: string) => {
            const selectedValueConfig = filter.config.values.find(
              valueConfig => valueConfig.id === selectedValue,
            )
            return selectedValueConfig.taggingId || selectedValueConfig.id
          })
          if (pivotsIds.includes(filter.config.id)) {
            playFilters.pivots[filterId] = filterValue
          } else {
            playFilters.filters[filterId] = filterValue
          }
        }
      })
    return playFilters
  }
}
